import { IHeaderCategories, IChild, IGrandChild } from "@/types/header";
import { Modal, Backdrop } from "@mui/material";
import Link from "@/components/core/localized-link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { decode } from "html-entities";
import useLocalizedRouter from "@/hooks/use-localized-router";

interface DesktopMenuProps {
  categories: IHeaderCategories[];
}

const HeaderDesktopMenu: React.FC<DesktopMenuProps> = ({ categories }) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const router = useLocalizedRouter();
  const currentPath = usePathname();

  const resetMenuState = () => {
    setOpen(false);
    setSelectedCategory(null);
  };

  useEffect(() => {
    resetMenuState();
  }, [currentPath]);

  useEffect(() => {
    const category = categories.find((cat) => cat.name === selectedCategory);
    if (category) {
      setOpen(category?.children?.length > 0 || false);
    }
  }, [selectedCategory, categories]);

  const handleMouseEnterNav = (categoryName: string) => {
    const category = categories.find((cat) => cat.name === decode(categoryName));
    setSelectedCategory(category?.children?.length ? category.name : null);
    setOpen(!!category?.children?.length);
  };

  const handleMouseLeave = (event: React.MouseEvent) => {
    const relatedTarget = event.relatedTarget as Node | null | undefined;

    if (!relatedTarget || relatedTarget instanceof Window) {
      resetMenuState();
      return;
    }
    if (
      relatedTarget &&
      (modalRef.current?.contains(relatedTarget) || headerRef.current?.contains(relatedTarget))
    ) {
      return;
    }

    resetMenuState();
  };

  const navigateToCategory = (href: string) => {
    const categorySlug = href.split("/").pop();
    router.push(`/category/${categorySlug}`);
    resetMenuState();
  };

  const renderGrandChildren = (grandChildren: IGrandChild[]) =>
    grandChildren.map((grandChild, index) => (
      <li className={grandChildClasses} key={index}>
        <Link href={`/category/${grandChild.href.split("/").pop()}`} prefetch={false}>
          <p className="text-[15px]"> {decode(grandChild.name)}</p>
        </Link>
      </li>
    ));

  const renderChildren = (children: IChild[]) =>
    children.map((child, index) => (
      <li key={index} className={childClasses}>
        <Link href={`/category/${child.href.split("/").pop()}`} prefetch={false}>
          <p className={linkClasses}>{decode(child.name)}</p>
        </Link>
        {child.grand_childs.length > 0 && (
          <ul className={grandChildListClasses}>{renderGrandChildren(child.grand_childs)}</ul>
        )}
      </li>
    ));

  return (
    <main
      className="relative hidden w-fit flex-wrap gap-7 bg-white py-4 md:flex"
      ref={headerRef}
      onMouseLeave={handleMouseLeave}
    >
      {categories.map((cat: IHeaderCategories, index: number) => (
        <div
          key={index}
          onMouseEnter={() => handleMouseEnterNav(cat.name)}
          className={getCategoryClasses(selectedCategory, cat.name)}
        >
          <p className="text-sm font-medium" onClick={() => navigateToCategory(cat.href)}>
            {decode(cat.name)}
          </p>
        </div>
      ))}
      <Modal
        open={open}
        disableAutoFocus
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: modalBackdropStyles,
        }}
        sx={{
          top: headerRef.current
            ? `${headerRef.current.offsetHeight + headerRef.current.offsetTop}px`
            : "250px",
          left: 0,
          right: 0,
          overflowY: "auto",
        }}
      >
        <div className="container relative px-[15px] md:px-2">
          <div
            className="pointer-events-auto relative top-[15px] z-[1] rounded-lg bg-white !p-10 shadow-[3]"
            id="modal-box"
            ref={modalRef}
            onMouseLeave={handleMouseLeave}
          >
            {selectedCategory && (
              <ul className={categoryListClasses}>
                {renderChildren(
                  categories.find((cat) => cat.name === selectedCategory)?.children || [],
                )}
              </ul>
            )}
          </div>
        </div>
      </Modal>
    </main>
  );
};

const getCategoryClasses = (selectedCategory: string | null, categoryName: string) => {
  return `
    relative cursor-pointer group 
    ${selectedCategory === categoryName || selectedCategory === null ? "text-black" : "text-gray-500"}
    after:content-[''] after:absolute after:left-0 after:bottom-[-16px] 
    after:w-0 after:h-[4px] after:bg-primary 
    after:transition-[width] after:duration-300 after:ease-[cubic-bezier(0.4,0,0.2,1)]
    group-hover:after:w-full
  `;
};

const modalBackdropStyles = {
  sx: {
    bgcolor: "rgba(0, 0, 0, 0.3)",
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: -1,
  },
};
const categoryListClasses = "grid grid-cols-4 gap-[20px]";

const childClasses = "inline-flex flex-col relative cursor-pointer p-0 w-auto items-start";

const linkClasses =
  "font-semibold relative inline-block text-[16px] " +
  "after:content-[''] after:absolute after:left-0 after:bottom-[-2px] " +
  "after:w-0 after:h-[1px] after:bg-black " +
  "after:transition-[width] after:duration-300 after:ease-[cubic-bezier(0.4,0,0.2,1)] " +
  "hover:after:w-full";

const grandChildListClasses = "flex flex-col gap-4 mt-5";

const grandChildClasses =
  "w-fit inline-block p-0 cursor-pointer relative " +
  "after:content-[''] after:absolute after:left-0 after:bottom-[-2px] " +
  "after:w-0 after:h-[1px] after:bg-black " +
  "after:transition-[width] after:duration-300 after:ease-[cubic-bezier(0.4,0,0.2,1)] " +
  "hover:after:w-full";

export default HeaderDesktopMenu;
